import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Chip, CircularProgress, Stack } from "@mui/material";
import { SystemDropdownOption } from "@shared";

import { useTranslation } from "@/translations";

interface Props {
  labelledById: string;
  options: SystemDropdownOption[];
  isLoading: boolean;
  selectedKeys: string[];
  onToggle: (key: string) => void;
  /**
   * Whether the section is marked after a submit attempt with no purpose (Dienst) selected. The chips keep
   * their normal colours; this only points the group at the section's error text.
   */
  hasError?: boolean;
  /** Id of the section's error text, so the group announces why it is marked. */
  describedById?: string;
}

/**
 * Multi-select of appointment purposes (Termin-Zwecke) as toggleable chips; each chip announces its state
 * via aria-pressed.
 */
export function PurposeChipSelect({
  labelledById,
  options,
  isLoading,
  selectedKeys,
  onToggle,
  hasError = false,
  describedById,
}: Props) {
  const { t } = useTranslation();

  if (isLoading) {
    return <CircularProgress size={20} aria-label={t("exchange.requestedServices.loading")} />;
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      sx={{ flexWrap: "wrap" }}
      role="group"
      aria-labelledby={labelledById}
      aria-describedby={hasError ? describedById : undefined}
    >
      {options.map((option) => {
        const selected = selectedKeys.includes(option.key);
        return (
          <Chip
            key={option.key}
            label={option.label}
            clickable
            color="primary"
            variant={selected ? "filled" : "outlined"}
            icon={selected ? <CheckRoundedIcon /> : undefined}
            aria-pressed={selected}
            onClick={() => onToggle(option.key)}
          />
        );
      })}
    </Stack>
  );
}
